import { useState, type FormEvent } from "react";
import { api, navigate, useApi } from "./api";
import { CopyButton, Empty, Loading, Modal, Notice, PageTitle } from "./common";
import { date, money } from "./format";
import type { Coupon, User } from "./types";

interface AccountEntry {
  id: string;
  type: string;
  status: string;
  email: string;
  password: string;
}

interface CouponEntry extends Coupon {
  couponType?: string;
  remainingCount?: number;
  status?: string;
}

interface InvitationsData {
  inviteCode: string;
  stats?: {
    availableRewardAmountCny?: number;
    totalRewardAmountCny?: number;
    frozenRewardAmountCny?: number;
    totalInviteCount?: number;
  };
  settings?: { enabled?: boolean };
  directInvites?: {
    id: string;
    email: string;
    status: string;
    createdAt: string;
  }[];
  rewardRecords?: {
    id: string | number;
    date: string;
    type: string;
    amountCny: number;
    status: string;
  }[];
  withdrawals?: {
    id: string | number;
    amountCny: number;
    status: string;
    createdAt: string;
    paidAt?: string;
    note?: string;
  }[];
}

function PurchasedAccount({ account }: { account: AccountEntry }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <article className="panel stack">
      <div>
        <h3>{account.type || "已购账号"}</h3>
        <button
          className="button subtle"
          aria-expanded={expanded}
          aria-controls={`account-${account.id}`}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "收起账号信息" : "查看账号信息"}
        </button>
      </div>
      {expanded && (
        <div id={`account-${account.id}`} className="stack">
          <div className="field">
            <span className="muted">账号</span>
            <code style={{ overflowWrap: "anywhere" }}>{account.email}</code>
            <CopyButton value={account.email} label="复制账号" />
          </div>
          <div className="field">
            <span className="muted">密码</span>
            <code style={{ overflowWrap: "anywhere" }}>
              {account.password || "未单独提供，请查看订单交付内容"}
            </code>
            {account.password && (
              <CopyButton value={account.password} label="复制密码" />
            )}
          </div>
        </div>
      )}
    </article>
  );
}

export default function Profile({
  user,
  onPasswordChanged,
}: {
  user: User;
  onPasswordChanged: () => void;
}) {
  const accounts = useApi<AccountEntry[]>("/user/account-info", user.id);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function changePassword(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setError("");
    if (newPassword !== confirmation) {
      setError("两次输入的新密码不一致");
      return;
    }
    if (newPassword === oldPassword) {
      setError("新密码不能与当前密码相同");
      return;
    }
    setBusy(true);
    try {
      await api("/user/update-password", { oldPassword, newPassword });
      setOldPassword("");
      setNewPassword("");
      setConfirmation("");
      onPasswordChanged();
    } catch (error) {
      setError((error as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <PageTitle
        title="个人中心"
        subtitle="管理账号信息、购买记录与账户安全。"
      />
      <section className="panel stack" aria-label="账户信息">
        <h2>{user.nickname || user.username || "我的账号"}</h2>
        <div className="metric-grid">
          <div className="metric">
            <span>邮箱</span>
            <strong style={{ overflowWrap: "anywhere", fontSize: "1rem" }}>
              {user.email}
            </strong>
          </div>
          <div className="metric">
            <span>账户余额</span>
            <strong>{money(user.balanceCny)}</strong>
          </div>
          <div className="metric">
            <span>注册时间</span>
            <strong style={{ fontSize: "1rem" }}>{date(user.createdAt)}</strong>
          </div>
        </div>
        <div className="field-row">
          <button
            className="button subtle"
            onClick={() => navigate("/transaction-record")}
          >
            我的订单
          </button>
          <button
            className="button subtle"
            onClick={() => navigate("/coupons")}
          >
            我的优惠券
          </button>
        </div>
      </section>

      <section className="stack" aria-labelledby="profile-purchased-title">
        <div>
          <h2 id="profile-purchased-title">已购账号</h2>
          <p className="muted">
            已交付并可识别的账号显示在这里，完整交付内容请查看对应订单。
          </p>
        </div>
        {accounts.loading ? (
          <Loading />
        ) : accounts.error ? (
          <div className="panel">
            <Notice>{accounts.error}</Notice>
            <button className="button subtle" onClick={accounts.reload}>
              重新加载
            </button>
          </div>
        ) : accounts.data?.length ? (
          <div className="grid-2">
            {accounts.data.map((account) => (
              <PurchasedAccount key={account.id} account={account} />
            ))}
          </div>
        ) : (
          <Empty
            title="暂无已购账号"
            text="购买并交付的成品账号会显示在这里。"
            action={
              <button
                className="button subtle"
                onClick={() => navigate("/transaction-record")}
              >
                查看我的订单
              </button>
            }
          />
        )}
      </section>

      <section className="panel stack" aria-labelledby="profile-password-title">
        <div>
          <h2 id="profile-password-title">修改密码</h2>
          <p className="muted">修改成功后需要使用新密码重新登录。</p>
        </div>
        <form className="form" onSubmit={changePassword}>
          <label className="field">
            当前密码
            <input
              type="password"
              autoComplete="current-password"
              required
              value={oldPassword}
              onChange={(event) => setOldPassword(event.target.value)}
            />
          </label>
          <div className="grid-2">
            <label className="field">
              新密码
              <input
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                placeholder="至少 8 位字符"
              />
            </label>
            <label className="field">
              再次输入新密码
              <input
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
              />
            </label>
          </div>
          <Notice>{error}</Notice>
          <button className="button primary" disabled={busy}>
            {busy ? "正在修改…" : "更新密码"}
          </button>
        </form>
      </section>
    </div>
  );
}

function couponStatus(coupon: CouponEntry): string {
  const status = coupon.effectiveStatus || coupon.status || "";
  if (status === "unused" && coupon.validEndTime) {
    const ends = new Date(coupon.validEndTime).getTime();
    if (Number.isFinite(ends) && ends < Date.now()) return "expired";
  }
  return status;
}

const couponLabels: Record<string, string> = {
  unused: "未使用",
  used: "已使用",
  expired: "已过期",
  disabled: "已停用",
};

export function Coupons() {
  const coupons = useApi<CouponEntry[]>("/user/coupons");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [filter, setFilter] = useState("all");
  const visible = (coupons.data || []).filter(
    (coupon) => filter === "all" || couponStatus(coupon) === filter,
  );

  async function redeem(event: FormEvent) {
    event.preventDefault();
    if (busy || !code.trim()) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await api("/user/coupons/redeem", { code: code.trim() });
      setCode("");
      setMessage("优惠券已领取");
      setFilter("unused");
      coupons.reload();
    } catch (error) {
      setError((error as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <PageTitle
        title="我的优惠券"
        subtitle="查看已领取的优惠券，适用商品与实际优惠以下单确认为准。"
      />
      <section className="panel stack" aria-labelledby="coupon-redeem-title">
        <h2 id="coupon-redeem-title">兑换优惠券</h2>
        <form className="form" onSubmit={redeem}>
          <label className="field">
            兑换码
            <div className="field-row">
              <input
                required
                maxLength={128}
                autoComplete="off"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder="输入优惠券兑换码"
              />
              <button
                className="button primary"
                disabled={busy || !code.trim()}
              >
                {busy ? "正在兑换…" : "兑换"}
              </button>
            </div>
          </label>
        </form>
        <Notice>{error}</Notice>
        <Notice kind="success">{message}</Notice>
      </section>
      <div className="field-row" aria-label="优惠券状态筛选">
        {[
          ["all", "全部"],
          ["unused", "未使用"],
          ["used", "已使用"],
          ["expired", "已过期"],
        ].map(([key, label]) => (
          <button
            className={`button ${filter === key ? "primary" : "subtle"}`}
            aria-pressed={filter === key}
            key={key}
            onClick={() => setFilter(key)}
          >
            {label}
          </button>
        ))}
      </div>
      {coupons.loading ? (
        <Loading />
      ) : coupons.error ? (
        <div className="panel">
          <Notice>{coupons.error}</Notice>
          <button className="button subtle" onClick={coupons.reload}>
            重新加载
          </button>
        </div>
      ) : !visible.length ? (
        <Empty
          title={
            filter === "all"
              ? "暂无优惠券"
              : `暂无${couponLabels[filter]}的优惠券`
          }
          text="领取后可在结算时查看适用优惠。"
        />
      ) : (
        <div className="grid-2">
          {visible.map((coupon) => {
            const status = couponStatus(coupon);
            const percent = coupon.couponType === "percent";
            const face = coupon.faceValueCny;
            const threshold = coupon.thresholdAmountCny;
            return (
              <article className="panel stack" key={coupon.id}>
                <div className="metric">
                  <span>{percent ? "比例优惠券" : "满减优惠券"}</span>
                  <strong>
                    {percent
                      ? typeof face === "number" &&
                        Number.isFinite(face) &&
                        face > 0
                        ? `最高优惠 ${money(face)}`
                        : "按比例优惠"
                      : money(face)}
                  </strong>
                </div>
                <h2>{coupon.name}</h2>
                <span className="badge">
                  {couponLabels[status] || "状态未知"}
                </span>
                <p className="muted">
                  {typeof threshold === "number" && Number.isFinite(threshold)
                    ? threshold > 0
                      ? `满 ${money(threshold)} 可用`
                      : "无最低订单金额要求"
                    : "使用门槛以订单确认为准"}
                </p>
                <p className="muted">
                  {coupon.validEndTime
                    ? `有效期至 ${date(coupon.validEndTime)}`
                    : "未设置到期时间"}
                </p>
                {typeof coupon.remainingCount === "number" && (
                  <p className="muted">剩余可用次数：{coupon.remainingCount}</p>
                )}
                {status === "unused" && (
                  <button
                    className="button subtle"
                    onClick={() => navigate("/subscribe")}
                  >
                    选择商品
                  </button>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

const withdrawalLabels: Record<string, string> = {
  pending: "待审核",
  paid: "已支付",
  cancelled: "已取消",
};

export function Invitations() {
  const invitations = useApi<InvitationsData>("/user/invitations");
  const [confirmAmount, setConfirmAmount] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [confirmationNotice, setConfirmationNotice] = useState("");
  const data = invitations.data;
  const available = data?.stats?.availableRewardAmountCny;
  const canWithdraw =
    typeof available === "number" &&
    Number.isFinite(available) &&
    available > 0 &&
    data?.settings?.enabled !== false;
  const inviteUrl = data?.inviteCode
    ? `${location.origin}/register?invite=${encodeURIComponent(data.inviteCode)}`
    : "";

  async function withdraw() {
    if (busy || confirmAmount === null) return;
    setBusy(true);
    setError("");
    setConfirmationNotice("");
    try {
      const current = await api<InvitationsData>("/user/invitations");
      const amount = current.stats?.availableRewardAmountCny;
      if (
        typeof amount !== "number" ||
        !Number.isFinite(amount) ||
        amount <= 0
      ) {
        setConfirmAmount(null);
        invitations.reload();
        throw new Error("当前已无可提现金额，请查看最新记录");
      }
      if (amount !== confirmAmount) {
        setConfirmAmount(amount);
        setConfirmationNotice("可提现金额已更新，请核对后再次确认。");
        return;
      }
      await api("/user/invitations/withdraw", {});
      setConfirmAmount(null);
      setMessage("提现申请已提交，等待人工审核。后续转账结果请查看提现记录。");
      invitations.reload();
    } catch (error) {
      setError((error as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (invitations.loading)
    return (
      <div className="stack">
        <PageTitle title="我的邀请" />
        <Loading />
      </div>
    );
  if (invitations.error)
    return (
      <div className="stack">
        <PageTitle title="我的邀请" />
        <Notice>{invitations.error}</Notice>
        <button className="button subtle" onClick={invitations.reload}>
          重新加载
        </button>
      </div>
    );

  return (
    <div className="stack">
      <PageTitle
        title="我的邀请"
        subtitle="查看邀请记录、奖励明细与提现状态。"
      />
      <Notice>{confirmAmount === null ? error : ""}</Notice>
      <Notice kind="success">{message}</Notice>
      <section className="panel stack" aria-label="邀请信息">
        <h2>我的邀请码</h2>
        {data?.inviteCode ? (
          <>
            <div className="field-row">
              <strong style={{ overflowWrap: "anywhere" }}>
                {data.inviteCode}
              </strong>
              <CopyButton value={data.inviteCode} label="复制邀请码" />
            </div>
            <div className="field">
              <label htmlFor="invitation-link">邀请链接</label>
              <input id="invitation-link" value={inviteUrl} readOnly />
              <CopyButton value={inviteUrl} label="复制邀请链接" />
            </div>
          </>
        ) : (
          <p className="muted">暂未提供邀请码。</p>
        )}
      </section>

      <div className="metric-grid">
        <div className="panel metric">
          <span>累计奖励</span>
          <strong>{money(data?.stats?.totalRewardAmountCny)}</strong>
        </div>
        <div className="panel metric">
          <span>待解冻奖励</span>
          <strong>{money(data?.stats?.frozenRewardAmountCny)}</strong>
        </div>
        <div className="panel metric">
          <span>可提现奖励</span>
          <strong>{money(available)}</strong>
          <button
            className="button primary"
            disabled={!canWithdraw}
            onClick={() => {
              setError("");
              setConfirmationNotice("");
              setConfirmAmount(available as number);
            }}
          >
            申请全额提现
          </button>
        </div>
      </div>

      <section className="panel stack" aria-labelledby="direct-invites-title">
        <h2 id="direct-invites-title">
          邀请记录
          {typeof data?.stats?.totalInviteCount === "number"
            ? ` · ${data.stats.totalInviteCount}`
            : ""}
        </h2>
        {!data?.directInvites?.length ? (
          <Empty
            title="暂无邀请记录"
            text="通过邀请码绑定的账号会显示在这里。"
          />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>账号</th>
                  <th>状态</th>
                  <th>时间</th>
                </tr>
              </thead>
              <tbody>
                {data.directInvites.map((invite) => (
                  <tr key={invite.id}>
                    <td>{invite.email}</td>
                    <td>
                      <span className="badge">{invite.status}</span>
                    </td>
                    <td>{date(invite.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <section className="panel stack" aria-labelledby="reward-records-title">
        <h2 id="reward-records-title">奖励明细</h2>
        {!data?.rewardRecords?.length ? (
          <Empty title="暂无奖励明细" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>类型</th>
                  <th>金额</th>
                  <th>状态</th>
                  <th>时间</th>
                </tr>
              </thead>
              <tbody>
                {data.rewardRecords.map((record) => (
                  <tr key={record.id}>
                    <td>{record.type}</td>
                    <td>{money(record.amountCny)}</td>
                    <td>
                      <span className="badge">{record.status}</span>
                    </td>
                    <td>{date(record.date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <section className="panel stack" aria-labelledby="withdraw-records-title">
        <h2 id="withdraw-records-title">提现记录</h2>
        {!data?.withdrawals?.length ? (
          <Empty title="暂无提现记录" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>金额</th>
                  <th>状态</th>
                  <th>申请时间</th>
                  <th>处理结果</th>
                </tr>
              </thead>
              <tbody>
                {data.withdrawals.map((record) => (
                  <tr key={record.id}>
                    <td>{money(record.amountCny)}</td>
                    <td>
                      <span className="badge">
                        {withdrawalLabels[record.status] || "状态未知"}
                      </span>
                    </td>
                    <td>{date(record.createdAt)}</td>
                    <td>
                      {record.paidAt
                        ? date(record.paidAt)
                        : record.note === "invitation_withdrawal_cancelled"
                          ? "提现已取消，金额退回"
                          : record.note || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {confirmAmount !== null && (
        <Modal
          title="确认全额提现"
          onClose={() => {
            if (!busy) {
              setConfirmAmount(null);
              setError("");
            }
          }}
        >
          <div className="stack">
            <p>
              本次将申请提现全部可提现奖励：
              <strong>{money(confirmAmount)}</strong>。
            </p>
            <p className="muted">
              提交后需人工审核与转账，并非立即到账。申请进度与处理结果可在提现记录中查看。
            </p>
            <Notice kind="info">{confirmationNotice}</Notice>
            <Notice>{error}</Notice>
            <div className="field-row">
              <button
                className="button subtle"
                disabled={busy}
                onClick={() => {
                  setConfirmAmount(null);
                  setError("");
                }}
              >
                取消
              </button>
              <button
                className="button primary"
                disabled={busy}
                onClick={withdraw}
              >
                {busy ? "正在提交…" : `确认申请 ${money(confirmAmount)}`}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
